import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  FILTER_FIELDS,
  STATUS_LABELS,
  dayBoundary,
  isoToDay,
  parseIdList,
  toIdList,
  type FilterKey,
} from '../../lib/business-filter-model';
import { formatMoney, parseMoney } from '../../lib/format';
import {
  customFields as customFieldsApi,
  listAttendants,
  lossReasons as lossReasonsApi,
  products as productsApi,
  tags as tagsApi,
} from '../../lib/resources';
import { Button, Field, Modal, inputClass } from '../ui';

const ID_OPERATORS: Array<[string, string]> = [
  ['some', 'Algum'],
  ['every', 'Todos'],
  ['none', 'Nenhum'],
];

const FIELD_OPERATORS: Array<[string, string]> = [
  ['contains', 'contém'],
  ['eq', 'é igual a'],
  ['not', 'não é'],
];

/** Os dados de apoio dos filtros (nomes de tag, produto, atendente…). */
function useFilterLookups(enabled = true) {
  const tags = useQuery({ queryKey: ['tags'], queryFn: () => tagsApi.list(), enabled });
  const products = useQuery({
    queryKey: ['products'],
    queryFn: () => productsApi.list({ take: 200 }),
    enabled,
  });
  const attendants = useQuery({ queryKey: ['attendants'], queryFn: listAttendants, enabled });
  const reasons = useQuery({
    queryKey: ['loss-reasons'],
    queryFn: () => lossReasonsApi.list(),
    enabled,
  });
  const businessFields = useQuery({
    queryKey: ['custom-fields', 'business'],
    queryFn: () => customFieldsApi.list({ entity: 'business' }),
    enabled,
  });
  const leadFields = useQuery({
    queryKey: ['custom-fields', 'lead'],
    queryFn: () => customFieldsApi.list({ entity: 'lead' }),
    enabled,
  });
  return {
    tags: tags.data?.data ?? [],
    products: products.data?.data ?? [],
    attendants: attendants.data?.data ?? [],
    reasons: reasons.data?.data ?? [],
    businessFields: businessFields.data?.data ?? [],
    leadFields: leadFields.data?.data ?? [],
  };
}

/** Texto curto do chip de um filtro ativo. */
function describe(
  key: FilterKey,
  value: string,
  lookups: ReturnType<typeof useFilterLookups>,
): string {
  const nameOf = (list: Array<{ id: string; name: string }>, id: string) =>
    list.find((item) => item.id === id)?.name ?? '…';
  switch (key) {
    case 'status':
      return STATUS_LABELS[value as keyof typeof STATUS_LABELS] ?? value;
    case 'attendant':
      return lookups.attendants.find((item) => item.userId === value)?.name ?? '…';
    case 'tags':
    case 'products': {
      const { operator, ids } = parseIdList(value);
      const list = key === 'tags' ? lookups.tags : lookups.products;
      const label = ID_OPERATORS.find(([op]) => op === operator)?.[1] ?? operator;
      return `${label}: ${ids.map((id) => nameOf(list, id)).join(', ')}`;
    }
    case 'minTotal':
    case 'maxTotal':
      return formatMoney(Number(value));
    case 'lossReasonId':
      return nameOf(lookups.reasons, value);
    case 'additionalField':
    case 'leadAdditionalField': {
      const [fieldId, operator, ...rest] = value.split(' ');
      const list = key === 'additionalField' ? lookups.businessFields : lookups.leadFields;
      const label = FIELD_OPERATORS.find(([op]) => op === operator)?.[1] ?? operator;
      return `${nameOf(list, fieldId)} ${label} "${rest.join(' ')}"`;
    }
    default:
      return key.endsWith('From') || key.endsWith('To')
        ? new Date(value).toLocaleDateString('pt-BR')
        : value;
  }
}

/** Chips removíveis dos filtros ativos, sob o cabeçalho. */
export function BusinessFilterChips({
  params,
  onRemove,
}: {
  params: URLSearchParams;
  onRemove: (key: FilterKey) => void;
}) {
  const active = FILTER_FIELDS.filter(({ key }) => params.get(key));
  const lookups = useFilterLookups(active.length > 0);
  if (active.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {active.map(({ key, label }) => (
        <span
          key={key}
          className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700"
        >
          <span className="text-slate-400">{label}:</span>
          {describe(key, params.get(key) ?? '', lookups)}
          <button
            type="button"
            aria-label={`Remover filtro ${label}`}
            onClick={() => onRemove(key)}
            className="ml-0.5 text-slate-400 hover:text-slate-700"
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}

function IdListFilter({
  value,
  options,
  onChange,
}: {
  value: string;
  options: Array<{ id: string; name: string }>;
  onChange: (value: string) => void;
}) {
  const { operator, ids } = parseIdList(value);
  return (
    <div className="mt-1 space-y-2">
      <select
        className={`${inputClass} mt-0`}
        value={operator}
        onChange={(event) => onChange(toIdList(event.target.value, ids))}
      >
        {ID_OPERATORS.map(([op, label]) => (
          <option key={op} value={op}>
            {label}
          </option>
        ))}
      </select>
      <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
        {options.map((option) => {
          const on = ids.includes(option.id);
          return (
            <button
              key={option.id}
              type="button"
              onClick={() =>
                onChange(
                  toIdList(
                    operator,
                    on ? ids.filter((id) => id !== option.id) : [...ids, option.id],
                  ),
                )
              }
              className={`rounded-full border px-2 py-0.5 text-xs ${
                on
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-300 text-slate-600'
              }`}
            >
              {option.name}
            </button>
          );
        })}
        {options.length === 0 && (
          <span className="text-xs text-slate-400">Nada cadastrado.</span>
        )}
      </div>
    </div>
  );
}

function FieldFilter({
  value,
  fields,
  onChange,
}: {
  value: string;
  fields: Array<{ id: string; name: string }>;
  onChange: (value: string) => void;
}) {
  const [fieldId = '', operator = 'contains', ...rest] = value.split(' ');
  const text = rest.join(' ');
  const update = (next: { fieldId?: string; operator?: string; text?: string }) => {
    const id = next.fieldId ?? fieldId;
    const content = next.text ?? text;
    onChange(
      id && content
        ? `${id} ${next.operator ?? operator} ${content}`
        : id
          ? `${id} ${next.operator ?? operator} `
          : '',
    );
  };
  return (
    <div className="mt-1 grid grid-cols-3 gap-2">
      <select
        className={`${inputClass} mt-0`}
        value={fieldId}
        onChange={(event) => update({ fieldId: event.target.value })}
      >
        <option value="">Campo…</option>
        {fields.map((field) => (
          <option key={field.id} value={field.id}>
            {field.name}
          </option>
        ))}
      </select>
      <select
        className={`${inputClass} mt-0`}
        value={operator}
        onChange={(event) => update({ operator: event.target.value })}
      >
        {FIELD_OPERATORS.map(([op, label]) => (
          <option key={op} value={op}>
            {label}
          </option>
        ))}
      </select>
      <input
        className={`${inputClass} mt-0`}
        value={text}
        placeholder="valor"
        onChange={(event) => update({ text: event.target.value })}
      />
    </div>
  );
}

/**
 * Painel "Filtros": 14 filtros com os nomes da API. Edita um rascunho e só
 * aplica no botão — a URL (e o Kanban) mudam de uma vez.
 */
export function BusinessFilterPanel({
  params,
  onApply,
  onClose,
}: {
  params: URLSearchParams;
  onApply: (next: Record<FilterKey, string>) => void;
  onClose: () => void;
}) {
  const lookups = useFilterLookups();
  const [draft, setDraft] = useState<Record<FilterKey, string>>(
    () =>
      Object.fromEntries(
        FILTER_FIELDS.map(({ key }) => [key, params.get(key) ?? '']),
      ) as Record<FilterKey, string>,
  );
  const set = (key: FilterKey, value: string) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const money = (key: 'minTotal' | 'maxTotal') =>
    draft[key] ? String(Number(draft[key]) / 100) : '';
  const day = (key: FilterKey, end: boolean) => (
    <input
      type="date"
      className={inputClass}
      value={isoToDay(draft[key])}
      onChange={(event) => set(key, dayBoundary(event.target.value, end))}
    />
  );

  return (
    <Modal title="Filtros" onClose={onClose} wide>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Status">
          <select
            className={inputClass}
            value={draft.status}
            onChange={(event) => set('status', event.target.value)}
          >
            <option value="">Todos (padrão)</option>
            {Object.entries(STATUS_LABELS)
              .filter(([key]) => key !== 'all')
              .map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Atendente">
          <select
            className={inputClass}
            value={draft.attendant}
            onChange={(event) => set('attendant', event.target.value)}
          >
            <option value="">Qualquer</option>
            {lookups.attendants.map((attendant) => (
              <option key={attendant.id} value={attendant.userId}>
                {attendant.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Tags do lead">
          <IdListFilter
            value={draft.tags}
            options={lookups.tags}
            onChange={(value) => set('tags', value)}
          />
        </Field>
        <Field label="Produtos">
          <IdListFilter
            value={draft.products}
            options={lookups.products}
            onChange={(value) => set('products', value)}
          />
        </Field>
        <Field label="Valor mínimo (R$)">
          <input
            className={inputClass}
            inputMode="decimal"
            value={money('minTotal')}
            onChange={(event) =>
              set('minTotal', event.target.value ? String(parseMoney(event.target.value)) : '')
            }
          />
        </Field>
        <Field label="Valor máximo (R$)">
          <input
            className={inputClass}
            inputMode="decimal"
            value={money('maxTotal')}
            onChange={(event) =>
              set('maxTotal', event.target.value ? String(parseMoney(event.target.value)) : '')
            }
          />
        </Field>
        <Field label="Motivo de perda">
          <select
            className={inputClass}
            value={draft.lossReasonId}
            onChange={(event) => set('lossReasonId', event.target.value)}
          >
            <option value="">Qualquer</option>
            {lookups.reasons.map((reason) => (
              <option key={reason.id} value={reason.id}>
                {reason.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Origem do lead (contém)">
          <input
            className={inputClass}
            value={draft.source}
            maxLength={120}
            onChange={(event) => set('source', event.target.value)}
          />
        </Field>
        <Field label="Movido desde">{day('lastMovedAtFrom', false)}</Field>
        <Field label="Movido até">{day('lastMovedAtTo', true)}</Field>
        <Field label="Ganho/perdido desde">{day('statusChangedAtFrom', false)}</Field>
        <Field label="Ganho/perdido até">{day('statusChangedAtTo', true)}</Field>
        <Field label="Campo adicional do negócio">
          <FieldFilter
            value={draft.additionalField}
            fields={lookups.businessFields}
            onChange={(value) => set('additionalField', value)}
          />
        </Field>
        <Field label="Campo adicional do lead">
          <FieldFilter
            value={draft.leadAdditionalField}
            fields={lookups.leadFields}
            onChange={(value) => set('leadAdditionalField', value)}
          />
        </Field>
      </div>
      <div className="mt-5 flex justify-between gap-2">
        <Button
          variant="ghost"
          onClick={() =>
            setDraft(
              Object.fromEntries(FILTER_FIELDS.map(({ key }) => [key, ''])) as Record<
                FilterKey,
                string
              >,
            )
          }
        >
          Limpar tudo
        </Button>
        <div className="flex gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            onClick={() => {
              const clean = Object.fromEntries(
                Object.entries(draft).map(([key, value]) => [
                  key,
                  // Campo adicional sem valor não é filtro.
                  key.endsWith('dditionalField') && value.trim().split(' ').length < 3
                    ? ''
                    : value,
                ]),
              ) as Record<FilterKey, string>;
              onApply(clean);
              onClose();
            }}
          >
            Aplicar filtros
          </Button>
        </div>
      </div>
    </Modal>
  );
}
